import { computed, ref, watchEffect, type ComputedRef, type Ref } from 'vue';
import { clampTarget, deriveTree, dragUnitOf, validTargets, type RowBlock } from './rowTree';
import type { VirtualWindow } from './types';
import { useDragTransform } from './useDragTransform';
import { cssSafeId } from './utils';

export type RowDropZone = {
  /** absolute row index the zone's top edge anchors to (center); may be out of window (CSS falls back to the container edge) */
  topAnchorIdx: number;
  /** absolute row index the zone's bottom edge anchors to (center) */
  bottomAnchorIdx: number;
  /** final, already-clamped drop target (`onRowMoved` "to" semantics) */
  target: number;
};

export function useRowDrag<T extends Record<string, unknown>>(params: {
  tableRef: Ref<HTMLElement | undefined>;
  data: ComputedRef<T[]>;
  rowIdKey: (value: T) => string;
  onRowMoved: ComputedRef<
    ((from: number, to: number, movedCount?: number) => Promise<void>) | undefined
  >;
  /* needed to know where the drop-zones are needed */
  virtualWindow: ComputedRef<VirtualWindow>;
  /* original index of the dragged row (set by `onRowDragStart`) */
  draggedRow: Ref<number | null>;
  /* parent/child grouping, arbitrarily deep (I-390/I-392); undefined = every row is its own unit */
  rowParentId?: ComputedRef<((row: T) => string | null) | undefined>;
}) {
  const draggedRowTargetIdx = ref<number | null>(null);
  const waitForRowMove = ref<boolean>(false);

  /**
   * Set true on the first dragenter after dragstart. Gates the bridge
   * drop-zone — only rendered after the user has moved off the source, so
   * Chrome doesn't cancel the drag on a zone overlapping the dragged row at
   * dragstart.
   */
  const hasMoved = ref(false);

  const { setOffset: setRowOffset, clearOffsets: clearRowOffsets } = useDragTransform(
    params.tableRef,
    'row'
  );

  // Warn once per message per composable instance — deriveTree re-runs on
  // every data change and would otherwise repeat itself.
  const warnedMessages = new Set<string>();
  const devWarn = import.meta.env.DEV
    ? (message: string) => {
        if (warnedMessages.has(message)) return;
        warnedMessages.add(message);
        console.warn(message);
      }
    : undefined;

  const treeInfo = computed(() => {
    const parentIdOf = params.rowParentId?.value;
    if (!parentIdOf) return null;
    return deriveTree(params.data.value, params.rowIdKey, parentIdOf, devWarn);
  });

  /** rows that travel together for the current drag (the whole subtree); null = no drag */
  const dragUnit = computed<RowBlock | null>(() => {
    const dragged = params.draggedRow.value;
    if (dragged === null) return null;
    const info = treeInfo.value;
    if (!info || dragged < 0 || dragged >= info.parentOfRow.length) {
      return { start: dragged, size: 1 };
    }
    return dragUnitOf(info, dragged);
  });

  /** sorted valid final targets; null = unrestricted (no rowParentId) */
  const validDropTargets = computed<number[] | null>(() => {
    const dragged = params.draggedRow.value;
    const info = treeInfo.value;
    if (dragged === null || !info || dragged < 0 || dragged >= info.parentOfRow.length) {
      return null;
    }
    return validTargets(info, dragged, params.data.value.length);
  });

  /**
   * One zone per row boundary k in [window.start, window.end], skipping the
   * boundaries inside/around the dragged unit (the bridge zone covers that
   * span). Targets are pre-clamped, so entering a zone always yields a valid
   * final position — invalid areas snap to the nearest legal insertion point.
   */
  const rowDropZones = computed<RowDropZone[]>(() => {
    const unit = dragUnit.value;
    if (!unit) return [];
    const { start, end } = params.virtualWindow.value;
    const valid = validDropTargets.value;
    const zones: RowDropZone[] = [];
    for (let k = start; k <= end; k++) {
      if (k >= unit.start && k <= unit.start + unit.size) continue;
      const raw = k <= unit.start ? k : k - unit.size;
      const target = valid ? clampTarget(valid, raw, unit.start) : raw;
      zones.push({ topAnchorIdx: k - 1, bottomAnchorIdx: k, target });
    }
    return zones;
  });

  /**
   * Late-emit bridge drop-zone descriptor. Covers the dragged unit's own
   * slot (the whole block), anchored to the rows immediately above and below
   * it (skipping over the unit itself, whose anchors follow the
   * transform-translated preview). Dropping here is a no-op.
   *
   * `null` means: no drag, or the user has not moved yet (zone must not be
   * rendered at dragstart — Chrome would cancel the drag).
   *
   * `topAnchorIdx`/`bottomAnchorIdx` are absolute row indices (NOT relative
   * to the virtual window), or `null` for the edge fallback.
   */
  const rowBridgeZone = computed<{
    topAnchorIdx: number | null;
    bottomAnchorIdx: number | null;
  } | null>(() => {
    const unit = dragUnit.value;
    if (!hasMoved.value || !unit) {
      return null;
    }
    const dataLen = params.data.value.length;
    if (unit.start < 0 || unit.start + unit.size > dataLen) {
      return null;
    }
    return {
      topAnchorIdx: unit.start > 0 ? unit.start - 1 : null,
      bottomAnchorIdx: unit.start + unit.size < dataLen ? unit.start + unit.size : null,
    };
  });

  // css-only row movement styles (calculated once/based on data-length)
  const rowTransformStyles = computed(() => {
    const map = new Map<string, { transform: string }>();
    for (const row of params.data.value) {
      const id = params.rowIdKey(row);
      map.set(id, {
        // Offset counts row heights (-1,0,1,2,...); `* 100%` = the row's own
        // height, so this only lines up under a uniform row height (see the
        // watchEffect below for the variable-height note).
        transform: `translateY(calc(var(--row-offset-${cssSafeId(id)}, 0) * 100%))`,
      });
    }
    return map;
  });

  function isRowInDragUnit(rowIdx: number): boolean {
    const unit = dragUnit.value;
    return !!unit && rowIdx >= unit.start && rowIdx < unit.start + unit.size;
  }

  function onRowDragStart(event: DragEvent, rowIdx: number) {
    hasMoved.value = false;
    params.draggedRow.value = rowIdx;
    draggedRowTargetIdx.value = dragUnit.value?.start ?? rowIdx;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  let rowDragEnterFrame: number | null = null;
  let pendingRowDragEnterTarget: number | null = null;
  // drop-zone enter -> set (pre-clamped) target
  function onRowDragEnter(target: number) {
    pendingRowDragEnterTarget = target;
    if (rowDragEnterFrame !== null) {
      return;
    }
    // rAF-throttling
    rowDragEnterFrame = requestAnimationFrame(() => {
      rowDragEnterFrame = null;
      const pending = pendingRowDragEnterTarget;
      pendingRowDragEnterTarget = null;
      if (pending === null || params.draggedRow.value === null) {
        return;
      }
      draggedRowTargetIdx.value = pending;
      hasMoved.value = true;
    });
  }

  // bridge enter -> snap back to the no-op position
  function onRowBridgeEnter() {
    const unit = dragUnit.value;
    if (!unit) return;
    onRowDragEnter(unit.start);
  }

  // drop-zone drop
  function onRowDrop() {
    const unit = dragUnit.value;
    if (
      unit === null ||
      draggedRowTargetIdx.value == null ||
      unit.start === draggedRowTargetIdx.value
    ) {
      return;
    }

    if (params.onRowMoved.value) {
      waitForRowMove.value = true;
      // Only pass movedCount when the parent/child mode is active so plain
      // tables keep the exact two-arg call shape.
      const move = treeInfo.value
        ? params.onRowMoved.value(unit.start, draggedRowTargetIdx.value, unit.size)
        : params.onRowMoved.value(unit.start, draggedRowTargetIdx.value);
      move.finally(() => {
        waitForRowMove.value = false;
        onRowDragEnd();
      });
    } else {
      onRowDragEnd();
    }
  }

  // cleanup
  function onRowDragEnd() {
    if (waitForRowMove.value) {
      return;
    }
    if (rowDragEnterFrame !== null) {
      cancelAnimationFrame(rowDragEnterFrame);
      rowDragEnterFrame = null;
      pendingRowDragEnterTarget = null;
    }
    waitForRowMove.value = false;
    hasMoved.value = false;
    params.draggedRow.value = null;
    draggedRowTargetIdx.value = null;
  }

  // CSS transform effect — moves the whole drag unit and shifts the in-between
  // rows by the unit size. Offsets are counted in ROW HEIGHTS (see
  // rowTransformStyles' `* 100%`), which assumes a uniform row height across the
  // table, exactly as the previous ±1 logic already did.
  //
  // For variable row heights this would need pixel offsets summed from the
  // measured heights in useVirtualScroll (total block height for the in-between
  // rows, total in-between height for the block rows) instead of the row-count
  // multiplier.
  watchEffect(() => {
    const unit = dragUnit.value;
    const targetIdx = draggedRowTargetIdx.value;
    const data = params.data.value;

    // recalculate row offsets
    clearRowOffsets();

    if (!unit || targetIdx === null || unit.start === targetIdx || !params.tableRef.value) {
      return;
    }
    const { start: bs, size: n } = unit;
    if (bs < 0 || bs + n > data.length || targetIdx < 0 || targetIdx + n > data.length) {
      return;
    }

    if (bs < targetIdx) {
      for (let i = bs + n; i < targetIdx + n; i++) {
        setRowOffset(params.rowIdKey(data[i]), `-${n}`);
      }
    } else {
      for (let i = targetIdx; i < bs; i++) {
        setRowOffset(params.rowIdKey(data[i]), `${n}`);
      }
    }
    const unitOffset = `${targetIdx - bs}`;
    for (let i = bs; i < bs + n; i++) {
      setRowOffset(params.rowIdKey(data[i]), unitOffset);
    }
  });

  return {
    draggedRowTargetIdx,
    waitForRowMove,
    rowDropZones,
    rowTransformStyles,
    isRowInDragUnit,
    onRowDragStart,
    onRowDragEnter,
    onRowBridgeEnter,
    onRowDrop,
    onRowDragEnd,
    hasMoved,
    rowBridgeZone,
  };
}
